/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApolloQueryResult, LazyQueryHookOptions, QueryHookOptions } from '@apollo/client';
import { FormikProvider, useFormik } from 'formik';
import _ from 'lodash';
import { ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useViewerRoles } from '@treams/treams-hooks';
import {
	AddIcon,
	Button,
	Card,
	Empty,
	FlexGap,
	Input,
	Paragraph,
	SearchIcon,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import SettingsItem from './SettingsItem';

type QueryResult<K extends string, T> = {
	data?: { [key in K]: { edges: { node: Partial<T> }[] } };
	refetch: () => Promise<ApolloQueryResult<unknown>>;
};

type LazyQueryResult<K extends string, T> = [
	(options?: unknown) => void,
	{
		data?: { [key in K]: { edges: { node: Partial<T> }[] } };
		refetch?: () => Promise<ApolloQueryResult<unknown>>;
	},
];

export type ArchiveRestoreMutationTuple = [
	(options: { variables: { input: { id: string } } }) => Promise<void>,
	{ loading: boolean },
];

export default function SettingsCollection<
	K extends string,
	V extends { isArchived?: boolean; first?: number; type?: string },
	T extends { id: string; isArchived: boolean },
>(props: {
	field: K;
	variables: Omit<V, 'isArchived'>;
	query?: (options: QueryHookOptions<any, any>) => QueryResult<K, T>;
	lazyQuery: (options: LazyQueryHookOptions<any, any>) => LazyQueryResult<K, T>;
	archive?: ArchiveRestoreMutationTuple;
	restore?: ArchiveRestoreMutationTuple;
	link: (item: T) => string;
	title: (item: T) => string;
	subheader?: ReactNode;
	translationKey: string;
	onAdd?: () => void;
	titleBadge?: (item: T) => ReactNode;
}) {
	const { t } = useTranslation();
	const { isHRBusinessPartner } = useViewerRoles();

	const formik = useFormik({
		initialValues: { query: '' },
		onSubmit: () => {},
	});

	const [showArchived, setShowArchived] = useState(false);

	const queries = {
		active: props.query?.({ variables: { ...props.variables, isArchived: false, first: 1000 } }),
		archive: props.lazyQuery({
			variables: { ...props.variables, isArchived: true, first: 1000 },
		}),
	};

	const [cache, setCache] = useState<{ node: Partial<T> }[] | undefined>(undefined);

	const all =
		cache ??
		(queries.active?.data?.[props.field].edges ?? [])
			.concat(queries.archive[1]?.data?.[props.field].edges ?? [])
			.filter(({ node }) =>
				props
					.title(node as T)
					.toLocaleLowerCase()
					.includes(formik.values.query.toLocaleLowerCase()),
			);

	const ref = useRef(all);
	ref.current = all;

	const items = all?.filter(({ node }) => !node.isArchived);
	const sortedItems = items && _.sortBy(items, ({ node }) => props.title(node as T));
	const archivedItems = queries.archive[1]?.data && all?.filter(({ node }) => node.isArchived);
	const archivedSortedItems =
		archivedItems && _.sortBy(archivedItems, ({ node }) => props.title(node as T));

	const onRefresh = async () => {
		setCache(ref.current);
		await queries.active?.refetch();
		await queries.archive[1]?.refetch?.();
		setCache(undefined);
	};

	const canChange = useMemo(() => {
		if (!isHRBusinessPartner) return true;

		return !['groups', 'departments', 'jobTitles', 'jobLevels'].includes(props.field);
	}, [props.field, isHRBusinessPartner]);

	const canArchive = useMemo(() => {
		if (!isHRBusinessPartner) return true;

		return !['cycleTemplates'].includes(props.field);
	}, [props.field, isHRBusinessPartner]);

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.page} width="100%">
			{props.query && (
				<FlexGap flexDirection="column" gap={tokens.gap.group} width="100%">
					<FlexGap alignItems="center" justifyContent="space-between" width="100%">
						<Paragraph mb={0} mr={3}>
							{t(`${props.translationKey}.description`)}
						</Paragraph>
						{canChange && props.onAdd && (
							<Button
								title={t('common.add')}
								onClick={props.onAdd}
								icon={<AddIcon />}
								type="secondary"
							/>
						)}
					</FlexGap>
					{props.subheader}
					<FormikProvider value={formik}>
						<Input
							search
							icon={<SearchIcon />}
							value={formik.values.query}
							onChange={(value) => formik.setFieldValue('query', value)}
							placeholder={t('common.search')}
						/>
					</FormikProvider>
					{sortedItems ? (
						sortedItems.length > 0 ? (
							<Card p={0}>
								{sortedItems.map(({ node }) => (
									<SettingsItem
										key={node.id}
										title={props.title}
										link={props.link}
										item={node as T}
										titleBadge={props.titleBadge?.(node as T)}
										archive={canChange && canArchive ? props.archive : undefined}
										restore={canChange && canArchive ? props.restore : undefined}
										onRefresh={onRefresh}
									/>
								))}
							</Card>
						) : (
							<Empty.Card title={t(`${props.translationKey}.empty`)} />
						)
					) : (
						<Spinner />
					)}
				</FlexGap>
			)}

			{showArchived ? (
				<FlexGap flexDirection="column" gap={tokens.gap.group} width="100%">
					<Paragraph mb={0}>{t(`${props.translationKey}.archived.description`)}</Paragraph>
					{archivedSortedItems ? (
						archivedSortedItems.length > 0 ? (
							<Card p={0}>
								{archivedSortedItems.map(({ node }) => (
									<SettingsItem
										key={node.id}
										title={props.title}
										link={props.link}
										item={node as T}
										accessory={props.titleBadge?.(node as T)}
										archive={canChange && canArchive ? props.archive : undefined}
										restore={canChange && canArchive ? props.restore : undefined}
										onRefresh={onRefresh}
									/>
								))}
							</Card>
						) : (
							<Empty.Card title={t(`${props.translationKey}.archived.empty`)} />
						)
					) : (
						<Spinner />
					)}
				</FlexGap>
			) : (
				<Button
					type="secondary"
					title={t(`${props.translationKey}.archived.button.title`)}
					onClick={() => {
						setShowArchived(true);
						queries.archive[0]();
					}}
				/>
			)}
		</FlexGap>
	);
}
